import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, LayoutDashboard, Users, Image, CalendarDays, Gift, MessageSquare,
  Calendar, User, Phone, MapPin, Car, Package, Timer, DollarSign, Trash2, RefreshCw,
  Clock, Mail, ImagePlus
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { services, serviceAddons, TRAVEL_FEE } from '../utils/mock';
import { toast } from 'sonner';
import MessagesPanel from './MessagesPanel';
import CustomersPanel from './admin/CustomersPanel';
import PhotoManagement from './admin/PhotoManagement';
import BookingCalendar from './admin/BookingCalendar';
import ReferralsPanel from './admin/ReferralsPanel';
import DashboardStats from './admin/DashboardStats';
import BookingsTable from './admin/BookingsTable';
import GalleryManagement from './admin/GalleryManagement';

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingTime, setEditingTime] = useState({});
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings', 'calendar', 'customers', 'photos', 'gallery', 'referrals', or 'messages'
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterStatus === 'all' 
        ? `${API_URL}/api/bookings`
        : `${API_URL}/api/bookings?status=${filterStatus}`;
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/admin');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      } else {
        toast.error('Failed to load bookings');
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Error loading bookings');
    } finally {
      setLoading(false);
    }
  }, [API_URL, filterStatus, navigate]);

  // Check authentication and fetch bookings
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const getAuthHeaders = () => {
    return {
      'Content-Type': 'application/json'
      // No Authorization header needed - cookie is sent automatically
    };
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Logged out successfully');
      navigate('/admin');
    } catch (error) {
      // Silent logout - just redirect
      navigate('/admin');
    }
  };

  // Calculate pricing for a booking
  const calculateBookingCost = (booking) => {
    let serviceCost = 0;
    let addonsCost = 0;
    const costs = [];

    // Find service price
    const service = services.find(s => s.title === booking.service);
    if (service && service.pricing && service.pricing[booking.vehicleType]) {
      serviceCost = service.pricing[booking.vehicleType];
      costs.push({
        item: booking.service,
        price: serviceCost
      });
    }

    // Calculate add-ons cost
    if (booking.addons && booking.addons.length > 0) {
      booking.addons.forEach(addonName => {
        const addon = serviceAddons.find(a => a.title === addonName);
        if (addon && addon.pricing && addon.pricing[booking.vehicleType]) {
          const addonPrice = addon.pricing[booking.vehicleType];
          addonsCost += addonPrice;
          costs.push({
            item: addonName,
            price: addonPrice
          });
        }
      });
    }

    // Add travel fee
    costs.push({
      item: 'Travel Fee',
      price: TRAVEL_FEE
    });

    return {
      serviceCost,
      addonsCost,
      travelFee: TRAVEL_FEE,
      totalCost: serviceCost + addonsCost + TRAVEL_FEE,
      itemizedCosts: costs
    };
  };

  // Calculate estimated duration for a booking (total time only)
  const calculateEstimatedDuration = (booking) => {
    const parseDuration = (durationStr) => {
      if (!durationStr) return [0, 0];
      
      const numbers = durationStr.match(/[\d.]+/g);
      if (!numbers) return [0, 0];
      
      const isHours = durationStr.toLowerCase().includes('hour');
      const multiplier = isHours ? 60 : 1;
      
      if (numbers.length >= 2) {
        return [parseFloat(numbers[0]) * multiplier, parseFloat(numbers[1]) * multiplier];
      }
      return [parseFloat(numbers[0]) * multiplier, parseFloat(numbers[0]) * multiplier];
    };
    
    let totalMin = 0;
    let totalMax = 0;
    
    // Add service duration
    const service = services.find(s => s.title === booking.service);
    if (service?.duration) {
      const [min, max] = parseDuration(service.duration);
      totalMin += min;
      totalMax += max;
    }
    
    // Add addon durations
    if (booking.addons && booking.addons.length > 0) {
      booking.addons.forEach(addonName => {
        const addon = serviceAddons.find(a => a.title === addonName);
        if (addon?.duration) {
          const [min, max] = parseDuration(addon.duration);
          totalMin += min;
          totalMax += max;
        }
      });
    }
    
    // Format total duration
    if (totalMin === 0 && totalMax === 0) {
      return 'Time varies';
    }
    
    if (totalMax >= 60) {
      const minHours = (totalMin / 60).toFixed(1).replace('.0', '');
      const maxHours = (totalMax / 60).toFixed(1).replace('.0', '');
      return minHours === maxHours ? `${minHours} hours` : `${minHours}-${maxHours} hours`;
    }
    
    return totalMin === totalMax ? `${totalMin} minutes` : `${totalMin}-${totalMax} minutes`;
  };

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include', // Include cookies
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success(`Booking ${newStatus}`);
        fetchBookings();
      } else {
        toast.error('Failed to update booking');
      }
    } catch (error) {
      toast.error('Error updating booking');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include', // Include cookies
      });

      if (response.ok) {
        toast.success('Booking deleted');
        fetchBookings();
      } else {
        toast.error('Failed to delete booking');
      }
    } catch (error) {
      toast.error('Error deleting booking');
    }
  };

  const updateEstimatedTime = async (bookingId, estimatedTime) => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ estimated_time: estimatedTime }),
      });

      if (response.ok) {
        toast.success('Estimated time updated');
        setEditingTime({ ...editingTime, [bookingId]: false });
        fetchBookings();
      } else {
        toast.error('Failed to update time');
      }
    } catch (error) {
      toast.error('Error updating time');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-600',
      confirmed: 'bg-blue-600',
      'in-progress': 'bg-orange-600',
      completed: 'bg-green-600',
      cancelled: 'bg-red-600'
    };
    return (
      <Badge className={`${statusColors[status]} text-white`}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-black py-12 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-gray-400">Manage your Mobile Shine bookings & messages</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex space-x-2 border-b border-zinc-800">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'bookings'
                ? 'text-red-500 border-b-2 border-red-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Bookings</span>
          </button>
          <button
            onClick={() => setActiveTab('customers')}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'customers'
                ? 'text-red-500 border-b-2 border-red-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Users className="w-5 h-5" />
            <span>Customers</span>
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'calendar'
                ? 'text-red-500 border-b-2 border-red-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <CalendarDays className="w-5 h-5" />
            <span>Calendar</span>
          </button>
          <button
            onClick={() => setActiveTab('photos')}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'photos'
                ? 'text-red-500 border-b-2 border-red-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Image className="w-5 h-5" />
            <span>Photos</span>
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'gallery'
                ? 'text-red-500 border-b-2 border-red-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <ImagePlus className="w-5 h-5" />
            <span>Gallery</span>
          </button>
          <button
            onClick={() => setActiveTab('referrals')}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'referrals'
                ? 'text-red-500 border-b-2 border-red-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Gift className="w-5 h-5" />
            <span>Referrals</span>
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'messages'
                ? 'text-red-500 border-b-2 border-red-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <MessageSquare className="w-5 h-5" />
            <span>Messages</span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'bookings' ? (
          <>
            {/* Stats */}
            <div className="grid md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-gray-400">Total Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{bookings.length}</div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-gray-400">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-500">
                {bookings.filter(b => b.status === 'pending').length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-gray-400">Confirmed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-500">
                {bookings.filter(b => b.status === 'confirmed').length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-gray-400">In Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-500">
                {bookings.filter(b => b.status === 'in-progress').length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-gray-400">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-500">
                {bookings.filter(b => b.status === 'completed').length}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800 border-green-500/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-gray-400">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-500">
                ${bookings.reduce((total, booking) => {
                  const pricing = calculateBookingCost(booking);
                  return total + pricing.totalCost;
                }, 0)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center space-x-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48 bg-zinc-900 border-zinc-800 text-white">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="text-white hover:bg-zinc-800">All Bookings</SelectItem>
                <SelectItem value="pending" className="text-white hover:bg-zinc-800">Pending</SelectItem>
                <SelectItem value="confirmed" className="text-white hover:bg-zinc-800">Confirmed</SelectItem>
                <SelectItem value="in-progress" className="text-white hover:bg-zinc-800">In Progress</SelectItem>
                <SelectItem value="completed" className="text-white hover:bg-zinc-800">Completed</SelectItem>
                <SelectItem value="cancelled" className="text-white hover:bg-zinc-800">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={fetchBookings}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-400">Loading bookings...</div>
          </div>
        ) : bookings.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 text-center">
              <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No bookings found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Card key={booking.id} className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl text-white flex items-center space-x-3">
                        <User className="w-5 h-5 text-red-500" />
                        <span>{booking.name}</span>
                      </CardTitle>
                      <CardDescription className="text-gray-400 mt-1">
                        Booking ID: {booking.id.slice(0, 8)}
                      </CardDescription>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(booking.status)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    {/* Contact Info */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Mail className="w-4 h-4 text-red-500" />
                        <span className="text-sm">{booking.email}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Phone className="w-4 h-4 text-red-500" />
                        <span className="text-sm">{booking.phone}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-300">
                        <MapPin className="w-4 h-4 text-red-500" />
                        <span className="text-sm">{booking.address}</span>
                      </div>
                    </div>

                    {/* Service Details */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Car className="w-4 h-4 text-red-500" />
                        <span className="text-sm">{booking.vehicleType}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Package className="w-4 h-4 text-red-500" />
                        <span className="text-sm">{booking.service}</span>
                      </div>
                      {booking.addons && booking.addons.length > 0 && (
                        <div className="text-sm text-gray-400">
                          Add-ons: {booking.addons.join(', ')}
                        </div>
                      )}
                    </div>

                    {/* Date & Time */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Calendar className="w-4 h-4 text-red-500" />
                        <span className="text-sm">{booking.date}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Clock className="w-4 h-4 text-red-500" />
                        <span className="text-sm">{booking.time}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Created: {formatDate(booking.created_at)}
                      </div>
                      
                      {/* Estimated Duration */}
                      <div className="mt-3 pt-3 border-t border-zinc-700">
                        <div className="flex items-center space-x-2 mb-2">
                          <Timer className="w-4 h-4 text-blue-500" />
                          <span className="text-xs text-gray-400 font-semibold">Est. Duration:</span>
                        </div>
                        <div className="text-sm text-blue-400 font-medium">
                          {calculateEstimatedDuration(booking)}
                        </div>
                        
                        {/* Custom Time Notes (Editable) */}
                        <div className="mt-3">
                          <div className="text-xs text-gray-500 mb-1">Custom Time Note:</div>
                          {editingTime[booking.id] ? (
                            <div className="flex items-center space-x-2">
                              <Input
                                type="text"
                                defaultValue={booking.estimated_time || ''}
                                placeholder="e.g., Running late, +30 mins"
                                className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    updateEstimatedTime(booking.id, e.target.value);
                                  }
                                }}
                                autoFocus
                              />
                              <Button
                                onClick={(e) => {
                                  const input = e.target.parentElement.querySelector('input');
                                  updateEstimatedTime(booking.id, input.value);
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs"
                              >
                                Save
                              </Button>
                            </div>
                          ) : (
                            <div
                              onClick={() => setEditingTime({ ...editingTime, [booking.id]: true })}
                              className="text-sm text-gray-400 cursor-pointer hover:text-blue-400 transition-colors italic"
                            >
                              {booking.estimated_time || 'Click to add notes'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {booking.notes && (
                    <div className="mb-4 p-3 bg-zinc-800 rounded-lg">
                      <div className="text-sm font-semibold text-gray-400 mb-1">Notes:</div>
                      <div className="text-sm text-gray-300">{booking.notes}</div>
                    </div>
                  )}

                  {/* Pricing Breakdown */}
                  {(() => {
                    const pricing = calculateBookingCost(booking);
                    return (
                      <div className="mb-4 p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                        <div className="flex items-center space-x-2 mb-3">
                          <DollarSign className="w-5 h-5 text-green-500" />
                          <div className="text-sm font-semibold text-white">Cost Breakdown</div>
                        </div>
                        <div className="space-y-2">
                          {pricing.itemizedCosts.map((item) => (
                            <div key={`${booking.id}-${item.item}`} className="flex justify-between text-sm">
                              <span className="text-gray-300">{item.item}</span>
                              <span className="text-gray-300 font-medium">${item.price}</span>
                            </div>
                          ))}
                          <div className="pt-2 mt-2 border-t border-zinc-700 flex justify-between">
                            <span className="text-white font-bold">Total Cost:</span>
                            <span className="text-green-500 font-bold text-lg">${pricing.totalCost}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-zinc-800">
                    {booking.status === 'pending' && (
                      <Button
                        onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                      >
                        Confirm
                      </Button>
                    )}
                    {booking.status === 'confirmed' && (
                      <>
                        <Button
                          onClick={() => updateBookingStatus(booking.id, 'in-progress')}
                          className="bg-orange-600 hover:bg-orange-700 text-white text-sm"
                        >
                          Start Detail
                        </Button>
                        <Button
                          onClick={() => updateBookingStatus(booking.id, 'completed')}
                          className="bg-green-600 hover:bg-green-700 text-white text-sm"
                        >
                          Mark Completed
                        </Button>
                      </>
                    )}
                    {booking.status === 'in-progress' && (
                      <Button
                        onClick={() => updateBookingStatus(booking.id, 'completed')}
                        className="bg-green-600 hover:bg-green-700 text-white text-sm"
                      >
                        Mark Completed
                      </Button>
                    )}
                    <Button
                      onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                      variant="outline"
                      className="border-zinc-700 text-gray-300 hover:bg-zinc-800 text-sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => deleteBooking(booking.id)}
                      variant="outline"
                      className="border-red-700 text-red-500 hover:bg-red-900/20 text-sm ml-auto"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </>
        ) : activeTab === 'customers' ? (
          /* Customers Tab */
          <CustomersPanel />
        ) : activeTab === 'calendar' ? (
          /* Calendar Tab */
          <BookingCalendar bookings={bookings} />
        ) : activeTab === 'photos' ? (
          /* Photos Tab */
          <PhotoManagement />
        ) : activeTab === 'gallery' ? (
          /* Gallery Tab */
          <GalleryManagement />
        ) : activeTab === 'referrals' ? (
          /* Referrals Tab */
          <ReferralsPanel />
        ) : (
          /* Messages Tab */
          <MessagesPanel />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
